import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Download, Trash2 } from "lucide-react";
import { useGuestSession } from "@/hooks/useGuestSession";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export function GuestDataMigration() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { guestResumeData, guestInterviewData, clearGuestData, hasGuestData } = useGuestSession();
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);

  useEffect(() => {
    // Show migration dialog when user logs in and has guest data
    if (user && hasGuestData()) {
      setShowMigrationDialog(true);
    }
  }, [user, hasGuestData]);

  const migrateGuestData = async () => {
    if (!user || (!guestResumeData && !guestInterviewData)) return;

    setIsMigrating(true);
    try {
      let migratedResume = null;

      // Migrate resume data if exists
      if (guestResumeData) {
        // Convert template ID to UUID if needed
        let templateUUID: string | null = null;
        if (guestResumeData.template_id) {
          try {
            const { getTemplateUUID } = await import('@/components/resume-builder/newtmp/templates/registry');
            templateUUID = getTemplateUUID(guestResumeData.template_id);
          } catch {
            console.warn('Could not convert template ID to UUID, skipping');
          }
        }

        const { data: resumeDocument, error: resumeError } = await supabase
          .from('rb_documents')
          .insert({
            user_id: user.id,
            content: guestResumeData.content as any,
            template_id: null,
            nts_template_id: templateUUID,
            version: 1,
            extraction_status: 'completed',
            parsing_status: 'completed'
          })
          .select()
          .single();

        if (resumeError) throw resumeError;
        migratedResume = resumeDocument;
      }

      // Migrate interview data if exists
      if (guestInterviewData && guestInterviewData.resumeText) {
        const { error: interviewError } = await supabase
          .from('resumes')
          .insert({
            user_id: user.id,
            file_name: 'Guest Session Resume',
            file_path: 'guest-session/resume.txt',
            content_type: 'text/plain',
            extracted_text: guestInterviewData.resumeText,
            analysis: guestInterviewData.analysisData || null
          });

        if (interviewError) throw interviewError;
      }

      // Clear guest data after successful migration
      clearGuestData();
      setShowMigrationDialog(false);
      
      toast.success("Your data has been transferred to your account successfully!");
      
      // Redirect to editor if resume was migrated, otherwise reload
      if (migratedResume?.id) {
        navigate(`/resume-builder/editor/${migratedResume.id}`);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error migrating guest data:', error);
      toast.error("Failed to transfer data. Please try again or contact support.");
    } finally {
      setIsMigrating(false);
    }
  };

  const discardGuestData = () => {
    clearGuestData();
    setShowMigrationDialog(false);
    toast.info("Guest data discarded");
  };

  if (!showMigrationDialog) return null;

  return (
    <Dialog open={showMigrationDialog} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Transfer Your Work
          </DialogTitle>
          <DialogDescription>
            We found data from your guest session. Would you like to transfer it to your account?
            <div className="mt-3 space-y-1 text-sm">
              {guestResumeData && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Resume data with {Object.keys(guestResumeData.content || {}).length} sections
                </div>
              )}
              {guestInterviewData && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Interview progress (Step {guestInterviewData.currentStep})
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        
        <DialogFooter className="flex-col space-y-2 sm:space-y-0 sm:flex-row">
          <Button 
            variant="outline" 
            onClick={discardGuestData}
            className="w-full sm:w-auto"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Discard
          </Button>
          <Button 
            onClick={migrateGuestData} 
            disabled={isMigrating}
            className="w-full sm:w-auto"
          >
            <Download className="mr-2 h-4 w-4" />
            {isMigrating ? "Transferring..." : "Transfer to Account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}